import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Plus, Pencil, Trash2, ImageIcon, X, GripVertical, Tag, Box } from "lucide-react";
import { publicApi, adminApi } from "../../lib/api";
import { resolveAsset } from "../../lib/assetUrl";
import { toast } from "sonner";

const emptyProduct = { name: "", subtitle: "", price: "", price_note: "", description: "", image: "", images: [], tag: "", options: [], model_3d: "" };

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [uploading, setUploading] = useState(false);
  const [newImageUrl, setNewImageUrl] = useState("");

  const load = () => publicApi.getProducts().then((r) => setProducts(r.data)).catch(console.error);
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyProduct); setNewImageUrl(""); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    const images = p.images && p.images.length > 0 ? p.images : (p.image ? [p.image] : []);
    setForm({
      name: p.name, subtitle: p.subtitle || "", price: p.price,
      price_note: p.price_note || "", description: p.description || "",
      image: p.image || "", images: images, tag: p.tag || "",
      options: p.options || [], model_3d: p.model_3d || "",
    });
    setNewImageUrl("");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        image: form.images.length > 0 ? form.images[0] : form.image,
        images: form.images,
        // Strip client-only _key from options before sending to backend
        options: form.options.map(({ _key, ...rest }) => rest),
      };
      if (editing) {
        await adminApi.updateProduct(editing, payload);
        toast.success("Product updated");
      } else {
        await adminApi.createProduct(payload);
        toast.success("Product created");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error saving product");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await adminApi.deleteProduct(id);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error("Error deleting product");
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminApi.uploadImage(file);
      // Store relative URL only - resolved at render time to survive env changes
      setForm((f) => ({ ...f, images: [...f.images, res.data.url] }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const addImageUrl = () => {
    if (!newImageUrl.trim()) return;
    setForm((f) => ({ ...f, images: [...f.images, newImageUrl.trim()] }));
    setNewImageUrl("");
  };

  const removeImage = (index) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== index) }));
  };

  // Options handlers
  const addOption = () => {
    setForm((f) => ({
      ...f,
      options: [
        ...f.options,
        { _key: `opt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, label: "", price: "" },
      ],
    }));
  };

  const updateOption = (index, field, value) => {
    setForm((f) => ({
      ...f,
      options: f.options.map((opt, i) => i === index ? { ...opt, [field]: value } : opt),
    }));
  };

  const removeOption = (index) => {
    setForm((f) => ({ ...f, options: f.options.filter((_, i) => i !== index) }));
  };

  const handleModelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["stl", "glb", "gltf"].includes(ext)) {
      toast.error("Only .stl, .glb, .gltf files are supported");
      return;
    }
    setUploading(true);
    try {
      const res = await adminApi.uploadImage(file);
      // Store relative URL only - resolved at render time to survive env changes
      setForm((f) => ({ ...f, model_3d: res.data.url }));
      toast.success("3D model uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const updateField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-slate-900">Products ({products.length})</h2>
        <Button onClick={openCreate} className="bg-sky-500 hover:bg-sky-600 text-white rounded-lg">
          <Plus className="w-4 h-4 mr-2" /> Add Product
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {products.map((p) => {
          const imgCount = p.images?.length || (p.image ? 1 : 0);
          const optCount = p.options?.length || 0;
          return (
            <Card key={p.id} className="border-slate-100 overflow-hidden">
              {(p.image || p.images?.[0]) && (
                <div className="h-40 overflow-hidden relative">
                  <img src={resolveAsset(p.images?.[0] || p.image)} alt={p.name} className="w-full h-full object-cover" />
                  <div className="absolute top-2 right-2 flex gap-1.5">
                    {imgCount > 1 && (
                      <span className="bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">{imgCount} imgs</span>
                    )}
                    {optCount > 0 && (
                      <span className="bg-sky-500/80 text-white text-xs px-2 py-0.5 rounded-full">{optCount} options</span>
                    )}
                  </div>
                </div>
              )}
              <CardContent className="p-4">
                <h3 className="font-bold text-slate-900">{p.name}</h3>
                <p className="text-sm text-sky-600">{p.subtitle}</p>
                <p className="text-lg font-bold text-slate-800 mt-1">{p.price}</p>
                {optCount > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {p.options.map((opt, i) => (
                      <span key={i} className="text-[11px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                        {opt.label}: {opt.price}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => openEdit(p)} className="flex-1">
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(p.id)} className="text-red-500 hover:text-red-600 hover:bg-red-50">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Product" : "Add Product"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => updateField("name", e.target.value)} placeholder="Product name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Subtitle</Label>
                <Input value={form.subtitle} onChange={(e) => updateField("subtitle", e.target.value)} placeholder="e.g. Memory Lamps" />
              </div>
              <div className="space-y-1.5">
                <Label>Tag</Label>
                <Input value={form.tag} onChange={(e) => updateField("tag", e.target.value)} placeholder="e.g. Popular" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Base Price</Label>
                <Input value={form.price} onChange={(e) => updateField("price", e.target.value)} placeholder="e.g. ₹550" />
              </div>
              <div className="space-y-1.5">
                <Label>Price Note</Label>
                <Input value={form.price_note} onChange={(e) => updateField("price_note", e.target.value)} placeholder="e.g. Starting from" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => updateField("description", e.target.value)} placeholder="Product description" rows={3} />
            </div>

            {/* ===== OPTIONS SECTION ===== */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5 text-sky-500" />
                  Price Options ({form.options.length})
                </Label>
                <Button type="button" variant="outline" size="sm" onClick={addOption} className="h-7 text-xs px-3">
                  <Plus className="w-3 h-3 mr-1" /> Add Option
                </Button>
              </div>

              {form.options.length > 0 ? (
                <div className="space-y-2 bg-slate-50 rounded-xl p-3">
                  {form.options.map((opt, idx) => (
                    <div key={opt._key || `existing-${idx}`} className="flex items-center gap-2 bg-white rounded-lg p-2 border border-slate-100 shadow-sm">
                      <GripVertical className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />
                      <Input
                        value={opt.label}
                        onChange={(e) => updateOption(idx, "label", e.target.value)}
                        placeholder="e.g. Small, Medium, Pack of 5"
                        className="h-8 text-sm flex-1 border-slate-200"
                      />
                      <Input
                        value={opt.price}
                        onChange={(e) => updateOption(idx, "price", e.target.value)}
                        placeholder="e.g. ₹550"
                        className="h-8 text-sm w-28 border-slate-200"
                      />
                      <button
                        type="button"
                        onClick={() => removeOption(idx)}
                        className="w-6 h-6 flex-shrink-0 rounded-full bg-red-50 hover:bg-red-100 flex items-center justify-center transition-colors"
                      >
                        <X className="w-3 h-3 text-red-500" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400 bg-slate-50 rounded-lg p-3 text-center">
                  No options yet. Add size/quantity variants with individual pricing.
                </p>
              )}
            </div>

            {/* ===== IMAGES SECTION ===== */}
            <div className="space-y-2">
              <Label>Product Images ({form.images.length})</Label>
              {form.images.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {form.images.map((img, idx) => (
                    <div key={idx} className="relative group rounded-lg overflow-hidden h-24 border border-slate-200">
                      <img src={resolveAsset(img)} alt={`Product ${idx + 1}`} className="w-full h-full object-cover" />
                      {idx === 0 && (
                        <span className="absolute top-1 left-1 bg-sky-500 text-white text-[10px] px-1.5 py-0.5 rounded">Cover</span>
                      )}
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  placeholder="Paste image URL"
                  className="flex-1"
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addImageUrl(); } }}
                />
                <Button type="button" variant="outline" onClick={addImageUrl} disabled={!newImageUrl.trim()}>
                  <Plus className="w-4 h-4" />
                </Button>
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                  <Button type="button" variant="outline" asChild disabled={uploading}>
                    <span><ImageIcon className="w-4 h-4" /></span>
                  </Button>
                </label>
              </div>
              <p className="text-xs text-slate-400">First image is used as cover. Add multiple for slideshow.</p>
            </div>

            {/* ===== 3D MODEL SECTION ===== */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <Box className="w-3.5 h-3.5 text-sky-500" />
                3D Model
              </Label>
              {form.model_3d ? (
                <div className="flex items-center gap-2 bg-slate-50 rounded-lg p-3 border border-slate-100">
                  <Box className="w-5 h-5 text-sky-500 flex-shrink-0" />
                  <span className="text-sm text-slate-600 truncate flex-1">{form.model_3d.split("/").pop()}</span>
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, model_3d: "" }))}
                    className="w-6 h-6 flex-shrink-0 rounded-full bg-red-50 hover:bg-red-100 flex items-center justify-center"
                  >
                    <X className="w-3 h-3 text-red-500" />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={form.model_3d}
                    onChange={(e) => updateField("model_3d", e.target.value)}
                    placeholder="Paste 3D model URL or upload"
                    className="flex-1"
                  />
                  <label className="cursor-pointer">
                    <input type="file" accept=".stl,.glb,.gltf" className="hidden" onChange={handleModelUpload} />
                    <Button type="button" variant="outline" asChild disabled={uploading}>
                      <span><Box className="w-4 h-4" /></span>
                    </Button>
                  </label>
                </div>
              )}
              <p className="text-xs text-slate-400">Upload .stl, .glb, or .gltf file. Customers can view the 3D model in the lightbox.</p>
            </div>

            <Button onClick={handleSave} className="w-full bg-sky-500 hover:bg-sky-600 text-white">
              {editing ? "Update Product" : "Create Product"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
